import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/who-we-are", label: "Who We Are" },
  { to: "/what-we-do", label: "What We Do" },
  { to: "/political-consulting", label: "Political Consulting" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      <header className="sticky top-0 z-50 bg-cream border-b border-ink/10" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" aria-label="Mosaicon Strategies home" onClick={() => setOpen(false)}>
            <Logo />
          </Link>
          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `link-underline text-[13px] tracking-[0.08em] uppercase font-medium transition-colors duration-300 ${
                    isActive ? "active text-burgundy" : "text-ink/70 hover:text-ink"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="lets-talk-btn"
              className="ml-2 bg-burgundy text-cream text-[13px] uppercase tracking-[0.18em] font-semibold px-6 py-3 transition-colors duration-300 hover:bg-ink focus:outline-none focus:ring-2 focus:ring-burgundy focus:ring-offset-2 focus:ring-offset-cream"
            >
              Let&apos;s Talk
            </Link>
          </nav>
          <button
            className="lg:hidden p-2 text-ink"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            key={location.pathname}
            className="fixed inset-0 z-[60] bg-cream flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
          >
            <div className="h-20 px-6 flex items-center justify-between border-b border-ink/10">
              <Logo />
              <button className="p-2" onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Mobile">
              {NAV.map((item, i) => (
                <span key={item.to} className="overflow-hidden">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.7, delay: 0.08 + i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <NavLink
                      to={item.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `font-serif text-4xl py-2 block tracking-tight ${isActive ? "text-burgundy" : "text-ink"}`
                      }
                    >
                      {item.label}
                    </NavLink>
                  </motion.span>
                </span>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5 }}
                className="mt-10"
              >
                <Link
                  to="/contact"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-lets-talk-btn"
                  className="inline-block bg-burgundy text-cream text-sm uppercase tracking-[0.2em] font-semibold px-8 py-4"
                >
                  Let&apos;s Talk
                </Link>
              </motion.div>
            </nav>
            <p className="px-8 pb-8 text-xs tracking-[0.2em] uppercase text-ink/50">
              Connecting communities | Opening possibilities | Navigating opportunities
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
